import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa';
import { Link, useLocation, useNavigate, Outlet, useParams } from 'react-router-dom';
import clsx from 'clsx';
import PaymentIcons from '../components/PaymentIcons';

// Subject Images for Backgrounds
import dietImage from '../assets/service-diet.jpg';
import dietmanImage from '../assets/service-dietman.jpg';
import hairImage from '../assets/service-hair.jpg';
import agamanImage from '../assets/service-agaman.jpg';
import skincareImage from '../assets/service-skincare.jpg';
import wellnessImage from '../assets/service-wellness.jpg';
import edImage from '../assets/service-ed.jpg';

/** ---------- 型定義（ファイル構造） ---------- */
type FaqItem = {
  id: string;
  question: string;
  answer: string;           // HTML 可
  category: string;         // 'service' | 'menu' | ...
  featured?: boolean;
  customComponent?: 'payment_icons';
};

type FaqCategory = {
  key: string;              // 'service' | 'menu' | ...
  title: string;
  description?: string;
};

type PickupEntry = {
  title: string;
  html: string;
  image?: string;
  category?: string;
  excerpt?: string;
};

type FaqContent = {
  categories: FaqCategory[];
  items: FaqItem[];
  pickup?: Record<string, PickupEntry>;
  pageText?: { title?: string; lead?: string };
};

/** ---------- Vite 子路径対応：/data の実URLを組み立て ---------- */
const dataUrl = (filename: string) => {
  // eslint-disable-next-line
  const base = process.env.PUBLIC_URL || '/';
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}data/${filename}`;
};

/** ---------- /public/data/faq_*.json から読み込み ---------- */
async function loadFaq(lang: 'ja' | 'zh'): Promise<FaqContent | null> {
  try {
    const res = await fetch(dataUrl(`faq_${lang}.json`), { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json()) as FaqContent;
    if (!Array.isArray(json?.categories) || !Array.isArray(json?.items)) return null;
    return json;
  } catch {
    return null;
  }
}

/** ---------- HTML/リッチテキストをプレーンテキストに ---------- */
function htmlToText(input: string): string {
  if (!input) return '';
  const s = String(input);
  if (s.indexOf('<') === -1 && s.indexOf('&') === -1) return s.trim();

  const pre = s
    .replace(/<\/\s*p\s*>/gi, '<br>')
    .replace(/<\s*\/li\s*>/gi, '</li><br>');

  const el = document.createElement('div');
  el.innerHTML = pre;

  el.querySelectorAll('li').forEach((li) => {
    if (!/^[\s・•-]/.test(li.textContent || '')) {
      li.insertAdjacentText('afterbegin', '・ ');
    }
  });

  let text = (el.textContent || el.innerText || '').replace(/\r\n/g, '\n');
  text = text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n');
  return text.trim();
}

/** ---------- FAQ メインページ（ファイル直読） ---------- */
const FAQPage: React.FC = () => {
  const { i18n } = useTranslation();
  const lang: 'ja' | 'zh' = i18n.language === 'zh' ? 'zh' : 'ja';

  const [content, setContent] = useState<FaqContent | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // 言語変化/初期表示でファイル読込
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      const data = await loadFaq(lang);
      if (!mounted) return;
      setContent(data);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [lang]);

  // 旧ハッシュ深リンク互換（例：#svc-01）
  const hashHandled = useRef(false);
  useEffect(() => {
    if (hashHandled.current) return;
    const h = decodeURIComponent(location.hash.replace('#', ''));
    if (h) {
      hashHandled.current = true;
      navigate(`/faq/pickup/${h}`, { replace: true });
    }
  }, [location.hash, navigate]);


  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = (id: string) => setOpenId((cur: string | null) => (cur === id ? null : id));

  const titleFallback = lang === 'ja' ? 'よくある質問（FAQ）' : '常见问题（FAQ）';
  const leadFallback =
    lang === 'ja'
      ? 'カテゴリからよくある質問と回答をご確認いただけます。'
      : '可按类别浏览常见问题与回答。';

  const pageTitle = content?.pageText?.title || titleFallback;
  const pageLead = content?.pageText?.lead || leadFallback;

  // JSON-LD（空時は空配列）
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (content?.items || []).slice(0, 24).map((it: FaqItem) => ({
      '@type': 'Question',
      name: it.question,
      acceptedAnswer: { '@type': 'Answer', text: htmlToText(it.answer) },
    })),
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-4 sm:pt-28 pb-20 bg-slate-50 font-sans">
        <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
          <div className="bg-white border-y sm:border border-slate-100 rounded-none sm:rounded-3xl shadow-none sm:shadow-sm p-5 sm:p-9">
            <h1 className="text-2xl font-semibold text-slate-900">
              {lang === 'ja' ? '読み込み中…' : '加载中…'}
            </h1>
          </div>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-slate-50 font-sans">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-white border border-amber-200 rounded-3xl shadow-sm p-7 md:p-9">
            <h1 className="text-2xl font-semibold text-amber-800">
              {lang === 'ja' ? 'FAQデータが見つかりません' : '未找到 FAQ 数据'}
            </h1>
            <p className="mt-2 text-amber-700">
              {lang === 'ja'
                ? 'public/data フォルダに faq_ja.json / faq_zh.json を配置してください。'
                : '请将 faq_ja.json / faq_zh.json 放到 public/data 目录下。'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 sm:pt-28 pb-20 bg-slate-50 font-sans" translate="no">
      <Helmet>
        <title>{pageTitle} - Melodis Clinic</title>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        {/* ヒーロー */}
        <section className="mb-4 sm:mb-8 text-center sm:text-left">
          <div className="bg-white border-y sm:border border-slate-100 rounded-none sm:rounded-3xl shadow-none sm:shadow-sm p-6 sm:p-12 bg-gradient-to-br from-white to-slate-50">
            <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">{pageTitle}</h1>
            <p className="mt-4 text-slate-600 text-lg max-w-2xl">{pageLead}</p>
          </div>
        </section>

        {/* 科目別 FAQ 入口（カード） - Redesigned & Moved Higher */}
        <section className="mb-10 sm:mb-16 px-4 sm:px-0">
          <div className="bg-white border border-slate-200 rounded-2xl sm:rounded-[2rem] shadow-sm p-6 sm:p-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 flex items-center">
              <span className="w-2 h-8 bg-sky-500 rounded-full mr-3" />
              {lang === 'ja' ? '科目別FAQで詳しく見る' : '按科目查看 FAQ 详情'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* AGA Male - Preparing */}
              <div className="relative group overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                {/* Background Image - Higher contrast for visibility */}
                <div className="absolute inset-0 z-0 opacity-[0.15] grayscale pointer-events-none">
                  <img src={agamanImage} alt="" className="w-full h-full object-cover scale-110" />
                </div>
                <div className="relative z-10 h-32 sm:h-36 p-4 sm:p-5 flex flex-col items-center justify-center text-center cursor-not-allowed">
                  <span className="text-base sm:text-[17px] font-bold text-slate-400">
                    {lang === 'ja' ? '男性AGA FAQ' : '男性AGA FAQ'}
                  </span>
                  <div className="mt-2 px-3 py-1 bg-slate-200/50 text-slate-500 text-[10px] font-bold rounded-full uppercase tracking-wider">
                    {lang === 'ja' ? '準備中' : '准备中'}
                  </div>
                </div>
              </div>

              {/* AGA Female - Preparing */}
              <div className="relative group overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                {/* Background Image - Higher contrast for visibility */}
                <div className="absolute inset-0 z-0 opacity-[0.15] grayscale pointer-events-none">
                  <img src={hairImage} alt="" className="w-full h-full object-cover scale-110" />
                </div>
                <div className="relative z-10 h-32 sm:h-36 p-4 sm:p-5 flex flex-col items-center justify-center text-center cursor-not-allowed">
                  <span className="text-base sm:text-[17px] font-bold text-slate-400">
                    {lang === 'ja' ? '女性AGA FAQ' : '女性AGA FAQ'}
                  </span>
                  <div className="mt-2 px-3 py-1 bg-slate-200/50 text-slate-500 text-[10px] font-bold rounded-full uppercase tracking-wider">
                    {lang === 'ja' ? '準備中' : '准备中'}
                  </div>
                </div>
              </div>

              {/* Diet Male */}
              <Link
                to="/faq/diet-male"
                className="group relative h-32 sm:h-36 rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-sky-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-center justify-center text-center shadow-sm"
              >
                {/* Background Image */}
                <div className="absolute inset-0 z-0 opacity-[0.12] group-hover:opacity-20 transition-opacity duration-300 pointer-events-none">
                  <img src={dietmanImage} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative z-10 p-4 sm:p-5 flex flex-col items-center">
                  <span className="text-base sm:text-[17px] font-bold text-slate-800 group-hover:text-sky-600 transition-colors">
                    {lang === 'ja' ? '男性メディカルダイエット FAQ' : '男性医疗减重 FAQ'}
                  </span>
                  <span className="mt-2 text-[11px] font-medium text-slate-400 group-hover:text-sky-400 flex items-center gap-1">
                    {lang === 'ja' ? '詳細を見る' : '查看详情'} <FaChevronDown className="-rotate-90 text-[10px]" />
                  </span>
                </div>
              </Link>

              {/* Diet Female */}
              <Link
                to="/faq/diet-female"
                className="group relative h-32 sm:h-36 rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-rose-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-center justify-center text-center shadow-sm"
              >
                {/* Background Image */}
                <div className="absolute inset-0 z-0 opacity-[0.12] group-hover:opacity-20 transition-opacity duration-300 pointer-events-none">
                  <img src={dietImage} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative z-10 p-4 sm:p-5 flex flex-col items-center">
                  <span className="text-base sm:text-[17px] font-bold text-slate-800 group-hover:text-rose-600 transition-colors">
                    {lang === 'ja' ? '女性メディカルダイエット FAQ' : '女性医疗减重 FAQ'}
                  </span>
                  <span className="mt-2 text-[11px] font-medium text-slate-400 group-hover:text-rose-400 flex items-center gap-1">
                    {lang === 'ja' ? '詳細を見る' : '查看详情'} <FaChevronDown className="-rotate-90 text-[10px]" />
                  </span>
                </div>
              </Link>

              {/* Medical Skincare */}
              <Link
                to="/faq/medical-skincare"
                className="group relative h-32 sm:h-36 rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-center justify-center text-center shadow-sm"
              >
                {/* Background Image */}
                <div className="absolute inset-0 z-0 opacity-[0.12] group-hover:opacity-20 transition-opacity duration-300 pointer-events-none">
                  <img src={skincareImage} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative z-10 p-4 sm:p-5 flex flex-col items-center">
                  <span className="text-base sm:text-[17px] font-bold text-slate-800 group-hover:text-emerald-600 transition-colors">
                    {lang === 'ja' ? 'メディカルスキンケア FAQ' : '医疗级护肤 FAQ'}
                  </span>
                  <span className="mt-2 text-[11px] font-medium text-slate-400 group-hover:text-emerald-400 flex items-center gap-1">
                    {lang === 'ja' ? '詳細を見る' : '查看详情'} <FaChevronDown className="-rotate-90 text-[10px]" />
                  </span>
                </div>
              </Link>

              {/* Low Dose Pill */}
              <Link
                to="/faq/pill-low-dose"
                className="group relative h-32 sm:h-36 rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-violet-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-center justify-center text-center shadow-sm"
              >
                {/* Background Image */}
                <div className="absolute inset-0 z-0 opacity-[0.12] group-hover:opacity-20 transition-opacity duration-300 pointer-events-none">
                  <img src={wellnessImage} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative z-10 p-4 sm:p-5 flex flex-col items-center">
                  <span className="text-base sm:text-[17px] font-bold text-slate-800 group-hover:text-violet-600 transition-colors">
                    {lang === 'ja' ? '低用量ピル FAQ' : '低用量药物 FAQ'}
                  </span>
                  <span className="mt-2 text-[11px] font-medium text-slate-400 group-hover:text-violet-400 flex items-center gap-1">
                    {lang === 'ja' ? '詳細を見る' : '查看详情'} <FaChevronDown className="-rotate-90 text-[10px]" />
                  </span>
                </div>
              </Link>

              {/* ED */}
              <Link
                to="/faq/ed"
                className="group relative h-32 sm:h-36 rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-blue-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-center justify-center text-center shadow-sm"
              >
                {/* Background Image */}
                <div className="absolute inset-0 z-0 opacity-[0.12] group-hover:opacity-20 transition-opacity duration-300 pointer-events-none">
                  <img src={edImage} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative z-10 p-4 sm:p-5 flex flex-col items-center">
                  <span className="text-base sm:text-[17px] font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                    ED FAQ
                  </span>
                  <span className="mt-2 text-[11px] font-medium text-slate-400 group-hover:text-blue-400 flex items-center gap-1">
                    {lang === 'ja' ? '詳細を見る' : '查看详情'} <FaChevronDown className="-rotate-90 text-[10px]" />
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* ピックアップ記事 (カード形式) */}
        {content.pickup && Object.keys(content.pickup).length > 0 && (
          <section className="mb-12 sm:mb-20 px-4 sm:px-0">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
              <span className="w-1.5 h-8 bg-coral rounded-full" />
              {lang === 'ja' ? 'ピックアップ記事' : '精选文章'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(content.pickup).map(([id, entry]) => (
                <div
                  key={id}
                  className="group bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col cursor-pointer"
                  onClick={() => navigate(`/faq/pickup/${id}`)}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={entry.image || '/images/about-bg.png'}
                      alt={entry.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    {entry.category && (
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-white/95 backdrop-blur-sm text-coral text-[10px] font-bold tracking-tight rounded-lg shadow-sm">
                          {entry.category}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-coral transition-colors line-clamp-2 leading-snug">
                      {entry.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-6 line-clamp-3">
                      {entry.excerpt}
                    </p>
                    <div className="mt-auto pt-5 border-t border-slate-50 flex items-center text-coral font-bold text-sm tracking-wide">
                      {lang === 'ja' ? '続きを読む' : '阅读更多'}
                      <svg
                        className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform duration-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* カテゴリ一覧 Anchor Link Tags */}
        <section className="mb-12 sm:mb-16 px-4 sm:px-0">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            {content.categories.map((c: FaqCategory) => (
              <a
                key={c.key}
                href={`#cat-${c.key}`}
                className="inline-flex items-center px-6 py-3 bg-white border border-slate-100 rounded-full shadow-sm hover:shadow-md hover:border-sky-200 hover:bg-sky-50 transition-all text-slate-700 font-medium text-sm group"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-sky-400 mr-2 transition-colors" />
                {c.title}
              </a>
            ))}
          </div>
        </section>

        {/* Q&A List Category-wise */}
        <section className="px-4 sm:px-0 space-y-16 sm:space-y-24">
          {content.categories.map((c: FaqCategory) => {
            const group = content.items.filter((it: FaqItem) => it.category === c.key);
            if (!group.length) return null;

            return (
              <div key={c.key} id={`cat-${c.key}`} className="scroll-mt-28">
                <div className="flex items-center mb-8 border-b border-slate-100 pb-4">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 flex items-center">
                    <span className="w-10 h-10 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mr-4 font-mono text-sm tracking-widest uppercase">#{c.key}</span>
                    {c.title}
                  </h3>
                </div>

                <div className="space-y-4">
                  {group.map((it: FaqItem) => {
                    const isOpen = it.id === openId;
                    const text = htmlToText(it.answer);
                    const paras = text ? text.split('\n').filter(Boolean) : [];

                    return (
                      <div
                        key={it.id}
                        id={`faq-${it.id}`}
                        className={clsx(
                          "bg-white border transition-all duration-300 rounded-[1.5rem] overflow-hidden",
                          isOpen ? "border-sky-300 shadow-xl ring-4 ring-sky-50" : "border-slate-100 hover:border-slate-200 shadow-sm"
                        )}
                      >
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-6 px-7 py-6 text-left transition-colors relative z-10"
                          aria-expanded={isOpen}
                          aria-controls={`panel-${it.id}`}
                          onClick={() => setOpenId(isOpen ? null : it.id)}
                        >
                          <span className={clsx("font-bold text-[17px] sm:text-lg leading-tight flex-1", isOpen ? "text-sky-700" : "text-slate-800")}>
                            {it.question}
                          </span>
                          <span className={clsx(
                            "flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-500 shrink-0",
                            isOpen ? "bg-sky-500 border-sky-500 rotate-180 text-white shadow-lg" : "bg-slate-50 border-slate-200 text-slate-400"
                          )}>
                            <FaChevronDown className="text-xs" />
                          </span>
                        </button>

                        <div
                          id={`panel-${it.id}`}
                          className={clsx(
                            "grid transition-[grid-template-rows] duration-500 ease-in-out",
                            isOpen ? "grid-rows-[1fr] border-t border-slate-100" : "grid-rows-[0fr]"
                          )}
                        >
                          <div className="overflow-hidden bg-slate-50/20">
                            <div className="px-8 py-8 sm:px-10 sm:py-10 text-slate-600 leading-relaxed">
                              <div className="flex gap-5">
                                <span className={clsx("w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl shrink-0 transition-colors duration-500", isOpen ? "bg-sky-100 text-sky-500 shadow-inner" : "bg-slate-100 text-slate-400")}>A</span>
                                <div className="space-y-4 pt-2.5 flex-1">
                                  {paras.length ? (
                                    paras.map((p, i) => (
                                      <p key={i} className="text-base sm:text-[17px] leading-relaxed">
                                        {p}
                                      </p>
                                    ))
                                  ) : (
                                    <p className="text-slate-400 italic">
                                      {lang === 'ja' ? '（回答が準備中です）' : '（答案准备中）'}
                                    </p>
                                  )}

                                  {it.customComponent === 'payment_icons' && (
                                    <div className="mt-8 p-6 bg-white rounded-2xl border border-slate-100 shadow-inner">
                                      <PaymentIcons />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      </div>

      {/* Modal Sub-route */}
      <Outlet />
    </div>
  );
};

export default FAQPage;

/** **********************************************************************
 * ピックアップ記事モーダル（/faq/pickup/:pid）
 *********************************************************************** */
export const FaqPickupModal: React.FC = () => {
  const { i18n } = useTranslation();
  const lang: 'ja' | 'zh' = i18n.language === 'zh' ? 'zh' : 'ja';
  const { pid } = useParams() as { pid?: string };
  const navigate = useNavigate();

  const [entry, setEntry] = useState<PickupEntry | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!pid) return;
      const data = await loadFaq(lang);
      if (!mounted) return;
      const pick = data?.pickup?.[pid] || null;
      setEntry(pick);
      if (!pick) {
        navigate('/faq', { replace: true });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [lang, pid, navigate]);

  if (!entry) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      onClick={() => navigate(-1)}
    >
      <div
        className="w-full max-w-4xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden max-h-[94vh] flex flex-col animate-in zoom-in-95 duration-500"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-56 md:h-80 shrink-0">
          <img
            src={entry.image || '/images/about-bg.png'}
            alt={entry.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
          <button
            onClick={() => navigate(-1)}
            aria-label="Close"
            className="absolute top-8 right-8 w-12 h-12 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-xl text-white rounded-full transition-all border border-white/40 shadow-xl group"
          >
            <span className="text-3xl leading-none transition-transform group-hover:rotate-90">×</span>
          </button>
          <div className="absolute bottom-8 left-10 right-10">
            {entry.category && (
              <span className="inline-block px-4 py-1.5 bg-emerald-500 text-white text-[10px] font-black tracking-widest uppercase rounded-lg mb-4 shadow-lg shadow-emerald-500/30">
                {entry.category}
              </span>
            )}
            <h3 className="text-3xl md:text-4xl font-extrabold text-white leading-tight drop-shadow-md">
              {entry.title}
            </h3>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-10 md:p-14 bg-slate-50/30">
          {entry.excerpt && (
            <div className="mb-10 p-7 bg-white rounded-3xl border border-slate-100 shadow-sm text-slate-700 italic leading-relaxed text-lg relative">
              <span className="absolute -top-4 -left-2 text-6xl text-slate-100 font-serif leading-none">“</span>
              {entry.excerpt}
            </div>
          )}
          <div
            className="prose prose-slate max-w-none 
              prose-headings:text-slate-900 prose-headings:font-extrabold
              prose-h4:text-xl prose-h4:mt-12 prose-h4:mb-6 prose-h4:pb-3 prose-h4:border-b-2 prose-h4:border-slate-100
              prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-8
              prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-8
              prose-li:text-slate-600 prose-li:mb-3
              text-[16px] md:text-[18px] leading-relaxed"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: entry.html }}
          />

          <div className="mt-16 pt-10 border-t border-slate-100 flex justify-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-3 px-10 py-4 bg-slate-900 text-white rounded-full hover:bg-black hover:scale-105 transition-all shadow-2xl hover:shadow-slate-300 active:scale-95 font-bold"
            >
              <svg className="w-5 h-5 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7-7 7" />
              </svg>
              {lang === 'ja' ? 'FAQ一覧に戻る' : '返回 FAQ 列表'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
